package pl.decks.personal.components.widgets.poissondiskgrid

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.dom.svg.Line
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import pl.decks.personal.components.widgets.rememberElementSize
import kotlin.math.*
import kotlin.random.Random

val meshStrokeColor by StyleVariable<Color>()
val meshStrokeWidth by StyleVariable<Int>()

@Composable
fun PointMesh(
    minDist: Double = 8.0,
    maxConnections: Int = 3,
    meshId: String? = null,
    modifier: Modifier = Modifier,
    renderPoint: @Composable (index: Int) -> Unit,
) {
    val mId = remember { meshId ?: "point-mesh-${Random.nextInt()}" }
    val points = remember(minDist) {
        poissonDiskSample(width = 100.0, height = 100.0, minDist = minDist).shuffled()
    }
    val connections = remember(points, maxConnections) {
        buildConnections(points, maxConnections)
    }
    val (boxWidth, boxHeight) = rememberElementSize(mId).value
    val color = meshStrokeColor.value(ColorMode.current.toPalette().color)
    val width = meshStrokeWidth.value(1)
    Box(Modifier.id(mId).then(modifier)) {
        if (boxWidth > 0 && boxHeight > 0) {
            Svg(
                attrs = Modifier.fillMaxWidth().fillMaxHeight()
                    .toAttrs {
                        attr("width", "100%")
                        attr("height", "100%")
                        attr("xmlns", "http://www.w3.org/2000/svg")
                    }
            ) {
                connections.forEach { (i, j) ->
                    val (x1, y1) = points[i]
                    val (x2, y2) = points[j]
                    Line(
                        Modifier.toAttrs {
                            attr("x1", (x1 / 100.0 * boxWidth).toString())
                            attr("y1", (y1 / 100.0 * boxHeight).toString())
                            attr("x2", (x2 / 100.0 * boxWidth).toString())
                            attr("y2", (y2 / 100.0 * boxHeight).toString())
                            attr("stroke", color.toString())
                            attr("stroke-width", width.toString())
                        }
                    )
                }
            }
            points.forEachIndexed { index, (nx, ny) ->
                val px = nx / 100.0 * boxWidth
                val py = ny / 100.0 * boxHeight

                Box(
                    Modifier
                        .position(Position.Relative)
                        .left(px.px)
                        .top(py.px)
                        .translate((-50).percent, (-50).percent),
                    contentAlignment = Alignment.Center,
                ) {
                    renderPoint(index)
                }
            }
        }
    }
}

private fun poissonDiskSample(
    width: Double,
    height: Double,
    minDist: Double,
    k: Int = 30,
): List<Pair<Double, Double>> {
    val cellSize = minDist / sqrt(2.0)
    val cols = ceil(width  / cellSize).toInt()
    val rows = ceil(height / cellSize).toInt()
    val grid = arrayOfNulls<Pair<Double, Double>>(cols * rows)

    fun gridIndex(x: Double, y: Double): Int {
        val col = (x / cellSize).toInt().coerceIn(0, cols - 1)
        val row = (y / cellSize).toInt().coerceIn(0, rows - 1)
        return col + row * cols
    }

    fun tooClose(x: Double, y: Double): Boolean {
        val gx = (x / cellSize).toInt().coerceIn(0, cols - 1)
        val gy = (y / cellSize).toInt().coerceIn(0, rows - 1)
        for (dy in -2..2) for (dx in -2..2) {
            val nx = gx + dx
            val ny = gy + dy
            if (nx !in 0..<cols || ny < 0 || ny >= rows) continue
            val neighbor = grid[nx + ny * cols] ?: continue
            if ((x - neighbor.first).pow(2) + (y - neighbor.second).pow(2) < minDist.pow(2)) return true
        }
        return false
    }

    val points = mutableListOf<Pair<Double, Double>>()
    val active = mutableListOf<Pair<Double, Double>>()

    fun addPoint(p: Pair<Double, Double>) {
        points += p
        active += p
        grid[gridIndex(p.first, p.second)] = p
    }

    addPoint(Pair(Random.nextDouble() * width, Random.nextDouble() * height))

    while (active.isNotEmpty()) {
        val origin = active.random()
        var found = false
        repeat(k) {
            if (found) return@repeat
            val angle = Random.nextDouble() * 2 * PI
            val radius = minDist * (1 + Random.nextDouble())
            val x = origin.first  + cos(angle) * radius
            val y = origin.second + sin(angle) * radius
            if (x in 0.0..width && y in 0.0..height && !tooClose(x, y)) {
                addPoint(Pair(x, y))
                found = true
            }
        }
        if (!found) active -= origin
    }

    return points
}

private fun buildConnections(
    points: List<Pair<Double, Double>>,
    maxConnections: Int,
): List<Pair<Int, Int>> {
    val connections = mutableSetOf<Pair<Int, Int>>()

    points.forEachIndexed { i, (x1, y1) ->
        points
            .mapIndexed { j, (x2, y2) ->
                j to (x1 - x2).pow(2) + (y1 - y2).pow(2)
            }
            .filter { (j, _) -> j != i }
            .sortedBy { (_, dist) -> dist }
            .take(maxConnections)
            .forEach { (j, _) ->
                connections += if (i < j) i to j else j to i
            }
    }

    return connections.toList()
}
